import React, { useState, useEffect } from 'react';
import { User, Lock, Smartphone, X, AtSign, Check, ChevronRight, Mail, Eye, EyeOff } from 'lucide-react';
import { PrimaryButton } from './ui/AtomicComponents';
import { toast } from 'react-toastify';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { Link } from 'react-router-dom';

const AuthModal = ({ isOpen, onClose, onLogin, initialView = 'login' }) => {
    const [loading, setLoading] = useState(false);
    // Views: 'login', 'register', 'recovery'
    const [currentView, setCurrentView] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [alias, setAlias] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Resetear estados al abrir/cerrar o cambiar modo
    useEffect(() => {
        if (isOpen) {
            setCurrentView(initialView === 'register' ? 'register' : 'login');
            setLoading(false);
            setEmail('');
            setPassword('');
            setAlias('');
            setTermsAccepted(false);
            setPrivacyAccepted(false);
            setShowPassword(false);
        }
    }, [isOpen, initialView]);

    if (!isOpen) return null;

    // Validación de contraseña
    const validatePassword = (pass) => {
        const hasNumber = /\d/.test(pass);
        const hasLetter = /[a-zA-Z]/.test(pass);
        const minLength = pass.length >= 8;
        return { hasNumber, hasLetter, minLength };
    };

    const passwordRequirements = validatePassword(password);
    const isPasswordValid = passwordRequirements.hasNumber && passwordRequirements.hasLetter && passwordRequirements.minLength;

    const handleAuth = async () => {
        if (!email && currentView !== 'recovery') return toast.warn("Ingresa tu correo");
        if ((!password) && currentView !== 'recovery') return toast.warn("Ingresa tu contraseña");

        if (currentView === 'register') {
            if (!alias) return toast.warn("El alias es obligatorio");
            if (!isPasswordValid) return toast.warn("La contraseña no cumple con los requisitos");
            if (!termsAccepted) return toast.warn("Debes aceptar los Términos y Condiciones");
            if (!privacyAccepted) return toast.warn("Debes aceptar la Política de Privacidad");
        }

        setLoading(true);

        try {
            if (currentView === 'register') {
                // Registrar nuevo usuario
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                // Crear perfil inicial
                await setDoc(doc(db, 'users', user.uid), {
                    email: user.email,
                    alias: alias,
                    balanceBs: "0.00",
                    balanceUsdt: "0.00",
                    bonusBalance: "0.00",
                    isAdmin: false,
                    createdAt: serverTimestamp(),
                    termsAccepted: true,
                    privacyAccepted: true
                });

                toast.success("¡Cuenta creada correctamente!");
                onClose();
                if (onLogin) onLogin();
            } else if (currentView === 'login') {
                // Login normal
                await signInWithEmailAndPassword(auth, email, password);
                toast.success("Bienvenido a PrimeApuesta");
                onClose();
                if (onLogin) onLogin();
            } else if (currentView === 'recovery') {
                // Recuperación de contraseña
                if (!email) {
                    toast.warn("Ingresa tu correo para recuperar la contraseña");
                    setLoading(false);
                    return;
                }
                await sendPasswordResetEmail(auth, email);
                toast.success("Enlace de recuperación enviado a tu correo");
                setCurrentView('login');
            }
        } catch (e) {
            console.error(e);
            let msg = "Error en la operación";
            if (e.code === 'auth/user-not-found' || e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') msg = "Credenciales inválidas";
            else if (e.code === 'auth/email-already-in-use') msg = "El correo ya está registrado";
            else if (e.code === 'auth/too-many-requests') msg = "Demasiados intentos. Intenta más tarde.";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const RequirementItem = ({ met, text }) => (
        <div className={`flex items-center gap-2 text-[10px] uppercase font-bold tracking-wider ${met ? 'text-emerald-500' : 'text-neutral-600'}`}>
            <div className={`w-3 h-3 rounded-full flex items-center justify-center border ${met ? 'border-emerald-500 bg-emerald-500/20' : 'border-neutral-700 bg-neutral-800'}`}>
                {met && <Check size={8} />}
            </div>
            {text}
        </div>
    );

    const getTitle = () => {
        switch (currentView) {
            case 'register': return 'Regístrese en PRIMEAPUESTA';
            case 'recovery': return 'Recuperar Acceso';
            default: return 'Iniciar Sesión';
        }
    };

    const getButtonText = () => {
        if (loading) return 'Procesando...';
        switch (currentView) {
            case 'register': return 'Registrarse';
            case 'recovery': return 'Enviar Enlace';
            default: return 'Acceder';
        }
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose}></div>
            <div className="relative w-full max-w-md bg-[#171717] rounded-[2.5rem] border border-white/5 shadow-2xl p-8 animate-in zoom-in-95 duration-200 overflow-y-auto max-h-[90vh] no-scrollbar">
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 text-neutral-500 hover:text-white transition-colors"
                >
                    <X size={20} />
                </button>

                <div className="text-center mb-6">
                    <h2 className="text-2xl font-black text-white italic tracking-tighter mb-1">
                        PRIME<span className="text-[#F59E0B]">APUESTA</span>
                    </h2>
                    <p className="text-neutral-500 text-[10px] font-black uppercase tracking-[0.3em]">
                        {getTitle()}
                    </p>
                </div>

                <div className="space-y-3">
                    {/* Email - Always visible */}
                    <div className="group bg-black/40 p-4 rounded-2xl border border-white/5 flex items-center gap-4 focus-within:border-[#F59E0B]/50 transition duration-300">
                        {currentView === 'recovery' ? (
                            <Mail className="text-neutral-700 group-focus-within:text-[#F59E0B] transition" size={20} />
                        ) : (
                            <User className="text-neutral-700 group-focus-within:text-[#F59E0B] transition" size={20} />
                        )}
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder={currentView === 'recovery' ? "Ingresa tu correo registrado" : "Correo electrónico"}
                            className="bg-transparent text-white w-full focus:outline-none text-sm placeholder:text-neutral-600 font-bold"
                        />
                    </div>

                    {/* Password - Hidden in Recovery */}
                    {currentView !== 'recovery' && (
                        <div className="group bg-black/40 p-4 rounded-2xl border border-white/5 flex items-center gap-4 focus-within:border-[#F59E0B]/50 transition duration-300">
                            <Lock className="text-neutral-700 group-focus-within:text-[#F59E0B] transition" size={20} />
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Contraseña"
                                className="bg-transparent text-white w-full focus:outline-none text-sm placeholder:text-neutral-600 font-bold"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="text-neutral-600 hover:text-white transition-colors"
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    )}

                    {/* Password Requirements (Only Register) */}
                    {currentView === 'register' && (
                        <div className="px-2 space-y-1.5 py-1">
                            <RequirementItem met={passwordRequirements.hasNumber} text="Al menos un carácter numérico" />
                            <RequirementItem met={passwordRequirements.hasLetter} text="Al menos un carácter alfabético" />
                            <RequirementItem met={passwordRequirements.minLength} text="Mínimo 8 caracteres" />
                        </div>
                    )}

                    {/* Alias (Only Register) */}
                    {currentView === 'register' && (
                        <div className="group bg-black/40 p-4 rounded-2xl border border-white/5 flex items-center gap-4 focus-within:border-[#F59E0B]/50 transition duration-300 mt-2">
                            <AtSign className="text-neutral-700 group-focus-within:text-[#F59E0B] transition" size={20} />
                            <input
                                type="text"
                                value={alias}
                                onChange={(e) => setAlias(e.target.value)}
                                placeholder="Alias de Usuario"
                                className="bg-transparent text-white w-full focus:outline-none text-sm placeholder:text-neutral-600 font-bold"
                            />
                        </div>
                    )}

                    {/* Terms & Conditions (Only Register) */}
                    {currentView === 'register' && (
                        <div className="space-y-3 mt-4 pt-2">
                            <label className="flex items-start gap-3 cursor-pointer group">
                                <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-all ${termsAccepted ? 'bg-[#F59E0B] border-[#F59E0B] text-black' : 'bg-transparent border-neutral-700 group-hover:border-neutral-500'}`}>
                                    {termsAccepted && <Check size={10} strokeWidth={4} />}
                                </div>
                                <input
                                    type="checkbox"
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                    className="hidden"
                                />
                                <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-wide leading-tight group-hover:text-neutral-300 transition-colors">
                                    He leído y acepto los <Link to="/terminos" target="_blank" onClick={(e) => e.stopPropagation()} className="text-[#F59E0B] underline decoration-1 underline-offset-2 hover:text-[#d97706] transition-colors">Términos y Condiciones</Link>.
                                </span>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer group">
                                <div className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-all ${privacyAccepted ? 'bg-[#F59E0B] border-[#F59E0B] text-black' : 'bg-transparent border-neutral-700 group-hover:border-neutral-500'}`}>
                                    {privacyAccepted && <Check size={10} strokeWidth={4} />}
                                </div>
                                <input
                                    type="checkbox"
                                    checked={privacyAccepted}
                                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                                    className="hidden"
                                />
                                <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-wide leading-tight group-hover:text-neutral-300 transition-colors">
                                    He leído y acepto la <Link to="/privacidad" target="_blank" onClick={(e) => e.stopPropagation()} className="text-[#F59E0B] underline decoration-1 underline-offset-2 hover:text-[#d97706] transition-colors">Política de Privacidad</Link>.
                                </span>
                            </label>
                        </div>
                    )}

                    {/* Recover Password Link (Only Login) */}
                    {currentView === 'login' && (
                        <div className="flex justify-end pt-1">
                            <button
                                onClick={() => setCurrentView('recovery')}
                                className="text-[10px] font-bold text-neutral-500 hover:text-[#F59E0B] transition-colors uppercase tracking-wider"
                            >
                                ¿Olvidó su clave?
                            </button>
                        </div>
                    )}
                </div>

                <div className="mt-8 space-y-4">
                    <button
                        onClick={handleAuth}
                        disabled={loading}
                        className="w-full bg-[#F59E0B] hover:bg-[#F59E0B]/90 disabled:opacity-50 disabled:cursor-not-allowed text-black py-4 rounded-xl font-black text-xs uppercase flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-[#F59E0B]/10"
                    >
                        {getButtonText()}
                        {!loading && <ChevronRight size={14} strokeWidth={3} />}
                    </button>

                    <button
                        onClick={() => {
                            if (currentView === 'login') setCurrentView('register');
                            else setCurrentView('login');

                            // Reset optional fields on switch
                            if (currentView !== 'recovery') {
                                setAlias('');
                                setTermsAccepted(false);
                                setPrivacyAccepted(false);
                            }
                        }}
                        className="w-full text-center py-2 text-[10px] font-black text-neutral-500 uppercase tracking-widest hover:text-[#F59E0B] transition"
                    >
                        {currentView === 'login' ? '¿No tienes cuenta? Regístrate Aquí' : '¿Ya tienes cuenta? Inicia Sesión'}
                    </button>

                    {currentView === 'recovery' && (
                        <button
                            onClick={() => setCurrentView('login')}
                            className="w-full text-center pt-2 text-[10px] font-black text-neutral-600 hover:text-white transition uppercase tracking-widest"
                        >
                            Cancelar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuthModal;
